import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Paper from "@material-ui/core/Paper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BarChartIcon from "@mui/icons-material/BarChart";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import { showQuiz, getResponses } from "../../services/quizService";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "24px",
    minHeight: "100%",
    background: "#f5f6fa",
  },
  topbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 24,
  },
  title: {
    fontWeight: 700,
    fontSize: 20,
    color: "#1a1a2e",
    flex: 1,
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 16,
    marginBottom: 24,
  },
  statCard: {
    background: "#fff",
    borderRadius: 12,
    padding: "18px 20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 800,
    color: "#1a1a2e",
  },
  statLabel: {
    fontSize: 13,
    color: "#6b7280",
  },
  tableWrapper: {
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  tableHead: {
    "& th": {
      background: `var(--primary-color, #6C63FF)`,
      color: "#fff",
      fontWeight: 700,
      fontSize: 13,
      padding: "12px 16px",
      whiteSpace: "nowrap",
    },
  },
  tableRow: {
    "&:nth-child(even)": { background: "#fafbfc" },
    "&:hover": { background: "#f3f4f6" },
  },
  tableCell: {
    fontSize: 13,
    color: "#374151",
    padding: "10px 16px",
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 24px",
    color: "#6b7280",
  },
}));

// Extrai dado de lead das respostas com base no tipo de etapa
function extractLeadField(steps, answers, type) {
  if (!steps || !answers) return "";
  const step = steps.find(s => s.type === type);
  if (!step) return "";
  return answers[String(step.id)] || "";
}

// Formata uma resposta para exibição
function formatAnswer(step, value) {
  if (value === undefined || value === null || value === "") return "—";
  if (Array.isArray(value)) return value.join(", ");
  if (step?.type === "yes_no") return value === "sim" ? "Sim" : "Não";
  if (step?.type === "single_choice") {
    const opt = (step.options || []).find(o => o.value === value);
    return opt ? opt.label : value;
  }
  return String(value);
}

export default function QuizRespostas() {
  const classes = useStyles();
  const theme = useTheme();
  const { id } = useParams();
  const history = useHistory();
  const primaryColor = theme.palette.primary.main || "#6C63FF";

  const [quiz, setQuiz] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [quizData, responsesData] = await Promise.all([
        showQuiz(id),
        getResponses(id),
      ]);
      setQuiz(quizData);
      setResponses(responsesData);
    } catch {
      toast.error("Erro ao carregar respostas");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
      <CircularProgress />
    </Box>
  );

  const steps = (quiz?.steps || []).sort((a, b) => a.orderIndex - b.orderIndex);
  const questionSteps = steps.filter(s => !["result", "redirect", "statement"].includes(s.type));

  const completed = responses.filter(r => r.completed).length;
  const avgScore = responses.length > 0
    ? Math.round(responses.filter(r => r.score).reduce((acc, r) => acc + (r.score || 0), 0) / responses.length)
    : 0;

  return (
    <Box className={classes.root} style={{ "--primary-color": primaryColor }}>
      {/* Topbar */}
      <Box className={classes.topbar}>
        <Tooltip title="Voltar">
          <IconButton size="small" onClick={() => history.push("/captacao/quiz")}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box flex={1}>
          <Typography className={classes.title}>
            <BarChartIcon style={{ verticalAlign: "middle", marginRight: 8, fontSize: 22 }} />
            Respostas — {quiz?.name}
          </Typography>
          <Typography style={{ fontSize: 13, color: "#9ca3af" }}>
            {quiz?.slug && `/${quiz.slug}`}
          </Typography>
        </Box>
        <Chip
          label={quiz?.status === "published" ? "Publicado" : "Rascunho"}
          color={quiz?.status === "published" ? "primary" : "default"}
          size="small"
        />
      </Box>

      {/* Stats */}
      <Box className={classes.stats}>
        <Box className={classes.statCard}>
          <Typography className={classes.statValue}>{responses.length}</Typography>
          <Typography className={classes.statLabel}>Total de respostas</Typography>
        </Box>
        <Box className={classes.statCard}>
          <Typography className={classes.statValue}>{completed}</Typography>
          <Typography className={classes.statLabel}>Concluídas</Typography>
        </Box>
        <Box className={classes.statCard}>
          <Typography className={classes.statValue}>
            {responses.length > 0 ? Math.round((completed / responses.length) * 100) : 0}%
          </Typography>
          <Typography className={classes.statLabel}>Taxa de conclusão</Typography>
        </Box>
        {avgScore > 0 && (
          <Box className={classes.statCard}>
            <Typography className={classes.statValue}>{avgScore}</Typography>
            <Typography className={classes.statLabel}>Score médio</Typography>
          </Box>
        )}
      </Box>

      {/* Tabela */}
      {responses.length === 0 ? (
        <Box className={classes.emptyState}>
          <BarChartIcon style={{ fontSize: 64, color: "#d1d5db", marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom>Nenhuma resposta ainda</Typography>
          <Typography variant="body2">
            Publique o quiz e compartilhe o link para começar a receber respostas.
          </Typography>
        </Box>
      ) : (
        <Paper className={classes.tableWrapper} elevation={0}>
          <Box style={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>
                    <PersonIcon style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }} />
                    Nome
                  </TableCell>
                  <TableCell>
                    <EmailIcon style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }} />
                    E-mail
                  </TableCell>
                  <TableCell>
                    <PhoneIcon style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }} />
                    Telefone
                  </TableCell>
                  {questionSteps
                    .filter(s => !["lead_name", "lead_email", "lead_phone"].includes(s.type))
                    .map(s => (
                      <TableCell key={s.id}>{s.question || s.type}</TableCell>
                    ))}
                  <TableCell>Score</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {responses.map((resp, idx) => {
                  const answers = resp.answers || {};
                  const nome = extractLeadField(steps, answers, "lead_name");
                  const email = extractLeadField(steps, answers, "lead_email");
                  const phone = extractLeadField(steps, answers, "lead_phone");

                  return (
                    <TableRow key={resp.id} className={classes.tableRow}>
                      <TableCell className={classes.tableCell} style={{ color: "#9ca3af" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {nome || <span style={{ color: "#d1d5db" }}>—</span>}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {email || <span style={{ color: "#d1d5db" }}>—</span>}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {phone || <span style={{ color: "#d1d5db" }}>—</span>}
                      </TableCell>
                      {questionSteps
                        .filter(s => !["lead_name", "lead_email", "lead_phone"].includes(s.type))
                        .map(s => (
                          <TableCell key={s.id} className={classes.tableCell}>
                            {formatAnswer(s, answers[String(s.id)])}
                          </TableCell>
                        ))}
                      <TableCell className={classes.tableCell}>
                        {resp.score != null ? resp.score : "—"}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Chip
                          label={resp.completed ? "Concluída" : "Parcial"}
                          size="small"
                          style={{
                            height: 20,
                            fontSize: 11,
                            background: resp.completed ? "#d1fae5" : "#fef3c7",
                            color: resp.completed ? "#065f46" : "#92400e",
                          }}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: "nowrap" }}>
                        {resp.createdAt
                          ? format(new Date(resp.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })
                          : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
